import { useState } from 'react';
import { Alert, App, Button, Card, Skeleton, Space, Typography } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { packApi } from '../api/packApi';
import { PackForm } from '../components/PackForm';
import { usePack } from '../hooks/usePack';
import { packErrorMessage } from '../utils/errors';
import { packToFormValues, toUpdatePackBody, type PackFormValues } from '../utils/packForm';

/**
 * Página de edición de Pack de Energía (`/packs/:packId/editar`, solo
 * `SUPER_ADMIN`). Carga el detalle con `usePack` y lo mapea al formulario. El PATCH
 * permite editar todos los campos del pack, incluido el estado; dejar la fecha de
 * fin vacía envía `validTo: null` y abre la vigencia.
 */
export function EditPackPage() {
  const { packId } = useParams<{ packId: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const { pack, loading, error, notFound, refresh } = usePack(packId);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const goToList = () => navigate('/packs');
  const goToDetail = () =>
    packId ? navigate(`/packs/${encodeURIComponent(packId)}`) : goToList();

  const handleSubmit = async (values: PackFormValues) => {
    if (!packId) return;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await packApi.update(packId, toUpdatePackBody(values));
      message.success('Pack actualizado.');
      goToDetail();
    } catch (caught) {
      setSubmitError(packErrorMessage(caught, 'No se pudo actualizar el pack.'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="packs-page">
      <div className="packs-page__header">
        <Space align="center">
          <Button
            type="text"
            icon={<ArrowLeftOutlined />}
            aria-label="Volver al detalle del pack"
            onClick={goToDetail}
          >
            Volver
          </Button>
          <Typography.Title level={3} style={{ margin: 0 }}>
            Editar pack
          </Typography.Title>
        </Space>
      </div>

      {loading ? (
        <Card>
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      ) : notFound ? (
        <Alert
          type="warning"
          showIcon
          message="No encontramos el pack"
          description="Puede que se haya eliminado o que el enlace esté incompleto."
          action={
            <Button size="small" onClick={goToList}>
              Volver al listado
            </Button>
          }
        />
      ) : error ? (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el pack"
          description={error}
          action={
            <Button size="small" onClick={refresh}>
              Reintentar
            </Button>
          }
        />
      ) : pack ? (
        <>
          {submitError && (
            <Alert
              type="error"
              showIcon
              role="alert"
              message="No se pudo actualizar el pack"
              description={submitError}
            />
          )}
          <Card>
            <PackForm
              mode="edit"
              initialValues={packToFormValues(pack)}
              submitting={submitting}
              onSubmit={handleSubmit}
              onCancel={goToDetail}
            />
          </Card>
        </>
      ) : null}
    </div>
  );
}
